/**
 * CustomerPaymentCard — Zahlungs-Einstellungen direkt im Profil-Tab (ohne Edit-Modus).
 * Admin sieht & ändert: Abrechnungsrhythmus, Auto-Belastung; Badge zeigt die Kunden-Präferenz.
 */
import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMING_LABELS = {
  sofort: '⚡ Sofort-Zahlung (bei Check-In)',
  taeglich: '📅 Täglich (automatisch)',
  ende_monat: '📅 Ende Monat (automatisch)',
};

const prefBadge = (customer) => {
  if (customer?.payment_preference === 'abo_interest') {
    return { label: '📋 Abo-Interesse', cls: 'bg-violet-100 text-violet-700 border-violet-200' };
  }
  if (customer?.auto_charge_card) {
    return { label: '⚡ Automatische Abbuchung', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
  }
  return { label: '🧾 Selbstzahler (Twint/Karte)', cls: 'bg-sky-100 text-sky-700 border-sky-200' };
};

export const CustomerPaymentCard = ({ customerDetails, setCustomerDetails }) => {
  const [saving, setSaving] = useState(false);

  const save = async (patch) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/customers/${customerDetails.id}/profile`, patch, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomerDetails({ ...customerDetails, ...patch });
      toast.success('Zahlungseinstellung gespeichert');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const badge = prefBadge(customerDetails);

  return (
    <div className="p-4 border border-gray-200 rounded-xl bg-white" data-testid="customer-payment-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-emerald-600" />
          Zahlung
        </h3>
        <Badge variant="outline" className={badge.cls} data-testid="payment-preference-badge">
          {badge.label}
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs text-gray-500">Zahlungsart & Abrechnungsrhythmus</Label>
          <Select
            value={customerDetails.payment_timing || 'sofort'}
            disabled={saving}
            onValueChange={(value) => save({ payment_timing: value })}
          >
            <SelectTrigger className="mt-1" data-testid="payment-timing-quick-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(TIMING_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
          <div>
            <p className="text-sm font-medium">Kreditkarte automatisch belasten</p>
            <p className="text-xs text-gray-500">
              {customerDetails.auto_charge_card
                ? 'Trainings werden der Karte belastet (falls hinterlegt)'
                : 'Kunde erhält Rechnung mit Zahlungslink (Twint/Karte)'}
            </p>
          </div>
          <Switch
            checked={!!customerDetails.auto_charge_card}
            disabled={saving}
            onCheckedChange={(v) => save({ auto_charge_card: !!v })}
            data-testid="auto-charge-quick-toggle"
          />
        </div>
      </div>
    </div>
  );
};
